import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useRef,
  useCallback,
  ReactNode,
} from 'react';
import { io, Socket } from 'socket.io-client';

// ----------- Type Definitions -----------
interface SocketEvent {
  event: string;
  data: any;
  timestamp: number;
}

interface SocketContextType {
  // Connection state
  isConnected: boolean;
  isConnecting: boolean;
  connectionError: string | null;

  // Socket instance
  socket: Socket | null;

  // Connection management
  connect: (url?: string, options?: any) => void;
  disconnect: () => void;
  reconnect: () => void;

  // Event handling
  emit: (event: string, data?: any) => void;
  on: (event: string, callback: (data: any) => void) => void;
  off: (event: string, callback?: (data: any) => void) => void;

  // Event history
  eventHistory: SocketEvent[];
  clearEventHistory: () => void;

  // Connection info
  connectionUrl: string | null;
  lastConnectedAt: Date | null;
  lastDisconnectedAt: Date | null;
}

// ----------- Context Create -----------
export const SocketContext = createContext<SocketContextType>({
  isConnected: false,
  isConnecting: false,
  connectionError: null,
  socket: null,
  connect: () => {},
  disconnect: () => {},
  reconnect: () => {},
  emit: () => {},
  on: () => {},
  off: () => {},
  eventHistory: [],
  clearEventHistory: () => {},
  connectionUrl: null,
  lastConnectedAt: null,
  lastDisconnectedAt: null,
});

// ----------- Provider Component -----------
export const SocketProvider: React.FC<{
  children: ReactNode;
  defaultUrl?: string;
  defaultOptions?: any;
}> = ({ children, defaultUrl, defaultOptions = {} }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [eventHistory, setEventHistory] = useState<SocketEvent[]>([]);
  const [connectionUrl, setConnectionUrl] = useState<string | null>(
    defaultUrl || null,
  );
  const [lastConnectedAt, setLastConnectedAt] = useState<Date | null>(null);
  const [lastDisconnectedAt, setLastDisconnectedAt] = useState<Date | null>(
    null,
  );

  const socketRef = useRef<Socket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;
  const reconnectDelay = 1000; // Start with 1 second

  // Add event to history
  const addEventToHistory = useCallback((event: string, data: any) => {
    const socketEvent: SocketEvent = {
      event,
      data,
      timestamp: Date.now(),
    };
    setEventHistory(prev => [...prev.slice(-99), socketEvent]); // Keep last 100 events
  }, []);

  // Clear event history
  const clearEventHistory = useCallback(() => {
    setEventHistory([]);
  }, []);

  // Handle connection events
  const handleConnect = useCallback(() => {
    console.log('Socket.IO: Connected successfully');
    setIsConnected(true);
    setIsConnecting(false);
    setConnectionError(null);
    setLastConnectedAt(new Date());
    reconnectAttemptsRef.current = 0;
    addEventToHistory('connect', { timestamp: new Date().toISOString() });
  }, [addEventToHistory]);

  const handleDisconnect = useCallback(
    (reason: string) => {
      console.log('Socket.IO: Disconnected', reason);
      setIsConnected(false);
      setIsConnecting(false);
      setLastDisconnectedAt(new Date());
      addEventToHistory('disconnect', {
        reason,
        timestamp: new Date().toISOString(),
      });

      // Auto-reconnect logic
      if (
        reason === 'io server disconnect' ||
        reason === 'io client disconnect'
      ) {
        // Server or client initiated disconnect - don't auto-reconnect
        return;
      }

      if (reconnectAttemptsRef.current < maxReconnectAttempts) {
        reconnectAttemptsRef.current++;
        const delay =
          reconnectDelay * Math.pow(2, reconnectAttemptsRef.current - 1); // Exponential backoff

        console.log(
          `Socket.IO: Attempting to reconnect in ${delay}ms (attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts})`,
        );

        reconnectTimeoutRef.current = setTimeout(() => {
          if (socketRef.current) {
            socketRef.current.connect();
          }
        }, delay);
      } else {
        setConnectionError(
          `Failed to reconnect after ${maxReconnectAttempts} attempts`,
        );
      }
    },
    [addEventToHistory],
  );

  const handleConnectError = useCallback(
    (error: Error) => {
      console.error('Socket.IO: Connection error', error);
      setIsConnecting(false);
      setConnectionError(error.message);
      addEventToHistory('connect_error', {
        error: error.message,
        timestamp: new Date().toISOString(),
      });
    },
    [addEventToHistory],
  );

  // Connect function
  const connect = useCallback(
    (url?: string, options: any = {}) => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }

      const socketUrl = url || defaultUrl;
      if (!socketUrl) {
        setConnectionError('No socket URL provided');
        return;
      }

      setIsConnecting(true);
      setConnectionError(null);
      setConnectionUrl(socketUrl);

      const socketOptions = {
        ...defaultOptions,
        ...options,
        autoConnect: true,
        reconnection: false, // We'll handle reconnection manually
      };

      console.log('Socket.IO: Connecting to', socketUrl);

      const socket = io(socketUrl, socketOptions);
      socketRef.current = socket;

      // Set up event listeners
      socket.on('connect', handleConnect);
      socket.on('disconnect', handleDisconnect);
      socket.on('connect_error', handleConnectError);

      // Listen for all events and add to history
      socket.onAny((eventName: string, ...args: any[]) => {
        addEventToHistory(eventName, args);
      });
    },
    [
      defaultUrl,
      defaultOptions,
      handleConnect,
      handleDisconnect,
      handleConnectError,
      addEventToHistory,
    ],
  );

  // Disconnect function
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (socketRef.current) {
      console.log('Socket.IO: Manually disconnecting');
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    setIsConnected(false);
    setIsConnecting(false);
    reconnectAttemptsRef.current = 0;
  }, []);

  // Reconnect function
  const reconnect = useCallback(() => {
    if (socketRef.current && connectionUrl) {
      disconnect();
      setTimeout(() => {
        connect(connectionUrl);
      }, 100);
    }
  }, [disconnect, connect, connectionUrl]);

  // Emit function
  const emit = useCallback(
    (event: string, data?: any) => {
      if (socketRef.current && isConnected) {
        socketRef.current.emit(event, data);
        addEventToHistory(`emit:${event}`, data);
      } else {
        console.warn('Socket.IO: Cannot emit event - not connected');
      }
    },
    [isConnected, addEventToHistory],
  );

  // On function
  const on = useCallback((event: string, callback: (data: any) => void) => {
    if (socketRef.current) {
      socketRef.current.on(event, callback);
    }
  }, []);

  // Off function
  const off = useCallback((event: string, callback?: (data: any) => void) => {
    if (socketRef.current) {
      if (callback) {
        socketRef.current.off(event, callback);
      } else {
        socketRef.current.off(event);
      }
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, []);

  const contextValue: SocketContextType = {
    isConnected,
    isConnecting,
    connectionError,
    socket: socketRef.current,
    connect,
    disconnect,
    reconnect,
    emit,
    on,
    off,
    eventHistory,
    clearEventHistory,
    connectionUrl,
    lastConnectedAt,
    lastDisconnectedAt,
  };

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  );
};

// ----------- Custom Hook -----------
export const useSocket = (): SocketContextType => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

// ----------- Utility Hooks -----------
export const useSocketEvent = (
  event: string,
  callback: (data: any) => void,
) => {
  const { on, off } = useSocket();

  useEffect(() => {
    on(event, callback);
    return () => off(event, callback);
  }, [event, callback, on, off]);
};

export const useSocketConnection = () => {
  const {
    isConnected,
    isConnecting,
    connectionError,
    connect,
    disconnect,
    reconnect,
  } = useSocket();
  return {
    isConnected,
    isConnecting,
    connectionError,
    connect,
    disconnect,
    reconnect,
  };
};
